/**
 * AeriosIQ brand assets — vector data verbatim from the designer file
 * `ARIOS2.1.svg` (delivered 2026-07-08, archived in the orchestration repo
 * under .vibe/_TRANSIST_FROM_WP_TO_AERIOSIQ/). No hand-tracing.
 *
 * The lockup is deliberately split into independently swappable parts
 * (transition doc 05 SS2): Mark (gradient network shape), Wordmark
 * ("Aerios" letterforms with dotless-i rect, currentColor), Badge
 * (gradient "IQ", Q as magnifier). Swap any part by editing its file only
 * — the composite AeriosIQLogo re-uses the exported path constants at the
 * designer's original coordinates, so spacing never drifts.
 *
 * Gradient (authoritative): #00829F at 0% -> #00303F at 77%.
 * Dark-surface treatment: deferred to the CI pass — the gradient is tuned
 * for light chrome; wordmark letters follow currentColor.
 */
import type { SVGProps } from "react";
import { useTheme } from "@mui/material/styles";

/**
 * Theme-aware fill (Christian, 2026-07-08): dark theme renders the ENTIRE
 * logo monochrome white (gradient's petrol end sinks into dark chrome);
 * light theme keeps the colored gradient. `variant` overrides the
 * auto-detection when a surface needs a fixed treatment.
 */
type LogoVariant = "auto" | "color" | "mono";

export const AERIOSIQ_BADGE_PATH =
  "M285,24.27h-4.09V.41h4.09v23.86ZM309.47,25.64l-3.25-3.25c-2.02,1.44-4.49,2.29-7.16,2.29-6.82,0-12.37-5.53-12.37-12.34s5.55-12.34,12.37-12.34,12.37,5.53,12.37,12.34c0,2.67-.86,5.14-2.31,7.16l3.25,3.25-2.89,2.89ZM299.06,20.77c4.67,0,8.47-3.78,8.47-8.44s-3.8-8.44-8.47-8.44-8.47,3.78-8.47,8.44,3.8,8.44,8.47,8.44Z";

/** The superscript "IQ" (Q styled as a magnifier), gradient-filled. */
export function AeriosIQBadge({
  variant = "auto",
  ...props
}: SVGProps<SVGSVGElement> & { variant?: LogoVariant }) {
  const mode = useTheme().palette.mode;
  const mono = variant === "mono" || (variant === "auto" && mode === "dark");
  return (
    <svg
      // eslint-disable-next-line no-restricted-syntax -- SVG namespace URI is canonical http://; not an OWASP V9.1.1 concern.
      xmlns="http://www.w3.org/2000/svg"
      viewBox="280.91 0 31.45 25.7"
      role="img"
      aria-label="IQ"
      height={14}
      {...props}
    >
      <defs>
        <linearGradient
          id="aiq-grad-badge"
          x1="280.91"
          y1="12.82"
          x2="312.36"
          y2="12.82"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#00829f" />
          <stop offset=".77" stopColor="#00303f" />
        </linearGradient>
      </defs>
      <path
        fill={mono ? "currentColor" : "url(#aiq-grad-badge)"}
        d={AERIOSIQ_BADGE_PATH}
      />
    </svg>
  );
}
